/**
 * Maps Connecticut town/city names (lowercase) to traditional county names.
 *
 * Connecticut abolished county government in 1960 and uses planning regions
 * as its formal administrative layer. As a result, Google Places API returns
 * planning region names (e.g., "Capitol Planning Region") for the
 * administrative_area_level_2 field instead of the 8 traditional county names
 * that the backend county list uses.
 *
 * This map is used as a fallback: when Google's county value does not match
 * any entry in the backend county list, the city/town name from the address
 * is used to look up the correct county.
 *
 * Includes common village names that Google returns in place of the official
 * parent town name (e.g., "Storrs" instead of "Mansfield").
 *
 * Source: Connecticut's 169 municipalities, stable since 1960.
 */
export const ctTownToCounty: Record<string, string> = {
  // FAIRFIELD COUNTY
  bethel: 'FAIRFIELD',
  bridgeport: 'FAIRFIELD',
  brookfield: 'FAIRFIELD',
  darien: 'FAIRFIELD',
  easton: 'FAIRFIELD',
  fairfield: 'FAIRFIELD',
  greenwich: 'FAIRFIELD',
  monroe: 'FAIRFIELD',
  'new canaan': 'FAIRFIELD',
  'new fairfield': 'FAIRFIELD',
  newtown: 'FAIRFIELD',
  norwalk: 'FAIRFIELD',
  redding: 'FAIRFIELD',
  ridgefield: 'FAIRFIELD',
  shelton: 'FAIRFIELD',
  sherman: 'FAIRFIELD',
  stamford: 'FAIRFIELD',
  stratford: 'FAIRFIELD',
  trumbull: 'FAIRFIELD',
  weston: 'FAIRFIELD',
  westport: 'FAIRFIELD',
  wilton: 'FAIRFIELD',
  // Common village/neighborhood names Google returns for Fairfield County towns
  'cos cob': 'FAIRFIELD',
  'old greenwich': 'FAIRFIELD',
  riverside: 'FAIRFIELD',
  byram: 'FAIRFIELD',
  glenville: 'FAIRFIELD',
  southport: 'FAIRFIELD',
  'sandy hook': 'FAIRFIELD',
  georgetown: 'FAIRFIELD',
  rowayton: 'FAIRFIELD',

  // HARTFORD COUNTY
  avon: 'HARTFORD',
  berlin: 'HARTFORD',
  bloomfield: 'HARTFORD',
  bristol: 'HARTFORD',
  burlington: 'HARTFORD',
  canton: 'HARTFORD',
  'east granby': 'HARTFORD',
  'east hartford': 'HARTFORD',
  'east windsor': 'HARTFORD',
  enfield: 'HARTFORD',
  farmington: 'HARTFORD',
  glastonbury: 'HARTFORD',
  granby: 'HARTFORD',
  hartford: 'HARTFORD',
  hartland: 'HARTFORD',
  manchester: 'HARTFORD',
  marlborough: 'HARTFORD',
  'new britain': 'HARTFORD',
  newington: 'HARTFORD',
  plainville: 'HARTFORD',
  'rocky hill': 'HARTFORD',
  simsbury: 'HARTFORD',
  southington: 'HARTFORD',
  'south windsor': 'HARTFORD',
  suffield: 'HARTFORD',
  'west hartford': 'HARTFORD',
  wethersfield: 'HARTFORD',
  windsor: 'HARTFORD',
  'windsor locks': 'HARTFORD',
  // Common village/neighborhood names Google returns for Hartford County towns
  collinsville: 'HARTFORD',
  unionville: 'HARTFORD',
  tariffville: 'HARTFORD',
  weatogue: 'HARTFORD',
  kensington: 'HARTFORD',
  plantsville: 'HARTFORD',
  'broad brook': 'HARTFORD',
  'warehouse point': 'HARTFORD',
  hazardville: 'HARTFORD',
  thompsonville: 'HARTFORD',

  // LITCHFIELD COUNTY
  barkhamsted: 'LITCHFIELD',
  bethlehem: 'LITCHFIELD',
  bridgewater: 'LITCHFIELD',
  canaan: 'LITCHFIELD',
  colebrook: 'LITCHFIELD',
  cornwall: 'LITCHFIELD',
  goshen: 'LITCHFIELD',
  harwinton: 'LITCHFIELD',
  kent: 'LITCHFIELD',
  litchfield: 'LITCHFIELD',
  morris: 'LITCHFIELD',
  'new hartford': 'LITCHFIELD',
  'new milford': 'LITCHFIELD',
  norfolk: 'LITCHFIELD',
  'north canaan': 'LITCHFIELD',
  plymouth: 'LITCHFIELD',
  roxbury: 'LITCHFIELD',
  salisbury: 'LITCHFIELD',
  sharon: 'LITCHFIELD',
  thomaston: 'LITCHFIELD',
  torrington: 'LITCHFIELD',
  warren: 'LITCHFIELD',
  washington: 'LITCHFIELD',
  watertown: 'LITCHFIELD',
  winchester: 'LITCHFIELD',
  woodbury: 'LITCHFIELD',
  // Common village/neighborhood names Google returns for Litchfield County towns
  winsted: 'LITCHFIELD',
  lakeville: 'LITCHFIELD',
  'falls village': 'LITCHFIELD',
  'cornwall bridge': 'LITCHFIELD',
  'west cornwall': 'LITCHFIELD',
  bantam: 'LITCHFIELD',
  terryville: 'LITCHFIELD',
  'washington depot': 'LITCHFIELD',

  // MIDDLESEX COUNTY
  chester: 'MIDDLESEX',
  clinton: 'MIDDLESEX',
  cromwell: 'MIDDLESEX',
  'deep river': 'MIDDLESEX',
  durham: 'MIDDLESEX',
  'east haddam': 'MIDDLESEX',
  'east hampton': 'MIDDLESEX',
  essex: 'MIDDLESEX',
  haddam: 'MIDDLESEX',
  killingworth: 'MIDDLESEX',
  middlefield: 'MIDDLESEX',
  middletown: 'MIDDLESEX',
  'old saybrook': 'MIDDLESEX',
  portland: 'MIDDLESEX',
  westbrook: 'MIDDLESEX',
  // Common village/neighborhood names Google returns for Middlesex County towns
  ivoryton: 'MIDDLESEX',
  centerbrook: 'MIDDLESEX',
  higganum: 'MIDDLESEX',
  cobalt: 'MIDDLESEX',
  moodus: 'MIDDLESEX',

  // NEW HAVEN COUNTY
  ansonia: 'NEW HAVEN',
  'beacon falls': 'NEW HAVEN',
  bethany: 'NEW HAVEN',
  branford: 'NEW HAVEN',
  cheshire: 'NEW HAVEN',
  derby: 'NEW HAVEN',
  'east haven': 'NEW HAVEN',
  guilford: 'NEW HAVEN',
  hamden: 'NEW HAVEN',
  madison: 'NEW HAVEN',
  meriden: 'NEW HAVEN',
  milford: 'NEW HAVEN',
  naugatuck: 'NEW HAVEN',
  'new haven': 'NEW HAVEN',
  'north branford': 'NEW HAVEN',
  'north haven': 'NEW HAVEN',
  orange: 'NEW HAVEN',
  oxford: 'NEW HAVEN',
  prospect: 'NEW HAVEN',
  seymour: 'NEW HAVEN',
  southbury: 'NEW HAVEN',
  wallingford: 'NEW HAVEN',
  waterbury: 'NEW HAVEN',
  'west haven': 'NEW HAVEN',
  wolcott: 'NEW HAVEN',
  woodbridge: 'NEW HAVEN',
  // Common village/neighborhood names Google returns for New Haven County towns
  northford: 'NEW HAVEN',
  'stony creek': 'NEW HAVEN',
  woodmont: 'NEW HAVEN',

  // NEW LONDON COUNTY
  bozrah: 'NEW LONDON',
  colchester: 'NEW LONDON',
  'east lyme': 'NEW LONDON',
  franklin: 'NEW LONDON',
  griswold: 'NEW LONDON',
  groton: 'NEW LONDON',
  lebanon: 'NEW LONDON',
  ledyard: 'NEW LONDON',
  lisbon: 'NEW LONDON',
  lyme: 'NEW LONDON',
  montville: 'NEW LONDON',
  'new london': 'NEW LONDON',
  'north stonington': 'NEW LONDON',
  norwich: 'NEW LONDON',
  'old lyme': 'NEW LONDON',
  preston: 'NEW LONDON',
  salem: 'NEW LONDON',
  sprague: 'NEW LONDON',
  stonington: 'NEW LONDON',
  voluntown: 'NEW LONDON',
  waterford: 'NEW LONDON',
  // Common village/neighborhood names Google returns for New London County towns
  mystic: 'NEW LONDON',
  pawcatuck: 'NEW LONDON',
  niantic: 'NEW LONDON',
  baltic: 'NEW LONDON',
  'jewett city': 'NEW LONDON',
  taftville: 'NEW LONDON',
  yantic: 'NEW LONDON',
  uncasville: 'NEW LONDON',
  oakdale: 'NEW LONDON',
  'gales ferry': 'NEW LONDON',

  // TOLLAND COUNTY
  andover: 'TOLLAND',
  bolton: 'TOLLAND',
  columbia: 'TOLLAND',
  coventry: 'TOLLAND',
  ellington: 'TOLLAND',
  hebron: 'TOLLAND',
  mansfield: 'TOLLAND',
  somers: 'TOLLAND',
  stafford: 'TOLLAND',
  tolland: 'TOLLAND',
  union: 'TOLLAND',
  vernon: 'TOLLAND',
  willington: 'TOLLAND',
  // Common village/neighborhood names Google returns for Tolland County towns
  rockville: 'TOLLAND',
  storrs: 'TOLLAND',
  'storrs mansfield': 'TOLLAND',
  'stafford springs': 'TOLLAND',
  'crystal lake': 'TOLLAND',
  amston: 'TOLLAND',

  // WINDHAM COUNTY
  ashford: 'WINDHAM',
  brooklyn: 'WINDHAM',
  canterbury: 'WINDHAM',
  chaplin: 'WINDHAM',
  eastford: 'WINDHAM',
  hampton: 'WINDHAM',
  killingly: 'WINDHAM',
  plainfield: 'WINDHAM',
  pomfret: 'WINDHAM',
  putnam: 'WINDHAM',
  scotland: 'WINDHAM',
  sterling: 'WINDHAM',
  thompson: 'WINDHAM',
  windham: 'WINDHAM',
  woodstock: 'WINDHAM',
  // Common village/neighborhood names Google returns for Windham County towns
  danielson: 'WINDHAM',
  willimantic: 'WINDHAM',
  moosup: 'WINDHAM',
  'central village': 'WINDHAM',
  dayville: 'WINDHAM',
  'north grosvenordale': 'WINDHAM',
  'grosvenor dale': 'WINDHAM',
  'east woodstock': 'WINDHAM',
  'south woodstock': 'WINDHAM',
  abington: 'WINDHAM',
};
